import { getPersistentJson } from '../../vrs-auth/storage';
export const PREVIEW_RESUME_MAX_AGE_MS = 4 * 60 * 60 * 1000;
let marker = null;
/**
 * Who is signed in, as one comparable string (role, tenant, id).
 *
 * @returns {string|null}
 */
function currentOwner() {
    const user = getPersistentJson('vrs_user_info');
    if (!user?.id) {
        return null;
    }
    return [user.role, user.tenantId ?? user.tenant_id, user.id].map(value => String(value ?? '')).join(':');
}
/**
 * Whether a marker written at `at` is still young enough to act on.
 *
 * @param {number} at - When the room took the camera.
 * @param {number} [now] - The current time.
 * @returns {boolean}
 */
export function isPreviewResumeFresh(at, now = Date.now()) {
    const age = now - at;
    return age >= 0 && age <= PREVIEW_RESUME_MAX_AGE_MS;
}
/**
 * A room is taking the camera from this surface: remember whether the
 * preview was on (written on or off, replacing any older marker).
 *
 * @param {PreviewSurface} surface - The surface being left.
 * @param {boolean} on - Whether its preview was on.
 * @param {number} [now] - The current time.
 * @returns {void}
 */
export function rememberPreviewForRoom(surface, on, now = Date.now()) {
    const owner = currentOwner();
    marker = owner ? {
        at: now,
        on,
        owner,
        surface
    } : null;
}
/**
 * Drops the marker: at sign-out (every surface; the next account never
 * inherits a preview), or for one surface whose camera came back without a
 * room (an offer that was declined or ran out).
 *
 * @param {PreviewSurface} [surface] - Only this surface's marker.
 * @returns {void}
 */
export function forgetPreviewForRoom(surface) {
    if (!surface || marker?.surface === surface) {
        marker = null;
    }
}
/**
 * Read once and removed whatever it holds. Only the surface and account that
 * left for the room, within the age limit, get `{ on, at }`; anything else is
 * null.
 *
 * @param {PreviewSurface} surface - The surface now showing.
 * @param {number} [now] - The current time.
 * @returns {IPreviewResume|null}
 */
export function takePreviewResume(surface, now = Date.now()) {
    const taken = marker;
    marker = null;
    if (!taken || taken.surface !== surface || taken.owner !== currentOwner() || !isPreviewResumeFresh(taken.at, now)) {
        return null;
    }
    return {
        at: taken.at,
        on: taken.on
    };
}
